import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Stack,
  Avatar,
  Paper,
  TextField,
  Button,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { Forum, Send, Reply, Close } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  useGetCommentsQuery,
  useAddCommentMutation,
} from "../../Store/api/queries";
import { showGlobalToast } from "../ToastProvider";

export default function CommentsSection({ targetId, targetType }) {
  const theme = useTheme();
  const navigate = useNavigate();

  const token = useSelector((state) => state.userSlice?.token);

  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);

  const [page, setPage] = useState(1);
  const [allComments, setAllComments] = useState([]);

  // رفرنس به المان انتهایی برای تشخیص اسکرول
  const observerRef = useRef(null);

  const queryArgs = useMemo(
    () => ({
      targetId,
      targetType,
      page,
      limit: 20,
      sort: "createdAt",
      order: "desc",
    }),
    [targetId, targetType, page],
  );

  const { data, isLoading, isFetching, isError, refetch } = useGetCommentsQuery(
    queryArgs,
    { skip: !targetId || !targetType },
  );

  const [addComment, { isLoading: isSubmitting }] = useAddCommentMutation();

  const comments = data?.data?.comments || [];
  const totalPages = data?.totalPages || 1;
  const hasMore = page < totalPages;

  // ریست کردن استیت‌ها هنگام تغییر تارگت
  useEffect(() => {
    setPage(1);
    setAllComments([]);
    setReplyTo(null);
    setText("");
  }, [targetId, targetType]);

  // مرج کردن نظرات جدید بدون ایجاد آیتم تکراری
  useEffect(() => {
    if (!comments.length) {
      if (page === 1) setAllComments([]);
      return;
    }

    setAllComments((prev) => {
      if (page === 1) return comments;
      const existingIds = new Set(prev.map((item) => item._id));
      const newItems = comments.filter((item) => !existingIds.has(item._id));
      return [...prev, ...newItems];
    });
  }, [comments, page]);

  // تعریف Intersection Observer برای تشخیص رسیدن به انتهای لیست
  const lastCommentRef = useCallback(
    (node) => {
      if (isFetching) return; // اگر در حال لود است درخواست جدید نفرستد
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prev) => prev + 1);
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [isFetching, hasMore],
  );

  // پاکسازی Observer در زمان Unmount شدن کامپوننت
  useEffect(() => {
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  // ✅ تاریخ + ساعت ایران (Asia/Tehran) با فرمت ۲۴ ساعته
  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleString("fa-IR", {
      timeZone: "Asia/Tehran",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const getAvatarSrc = (image) => {
    if (!image) return "";
    if (image.startsWith("http")) return image;

    const baseUrl = import.meta.env.VITE_URL || "";
    return `${baseUrl}${image}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      showGlobalToast("برای ثبت دیدگاه ابتدا وارد شوید", "error");
      navigate("/auth");
      return;
    }

    const trimmedText = text.trim();
    if (!trimmedText) return;

    try {
      await addComment({
        targetId,
        targetType,
        text: trimmedText,
        parentId: replyTo?._id || undefined,
      }).unwrap();

      setText("");
      setReplyTo(null);
      setPage(1);
      setAllComments([]);
      await refetch();

      showGlobalToast("دیدگاه شما ثبت شد و پس از تایید نمایش داده می‌شود");
    } catch (error) {
      console.error("Error adding comment:", error);
      showGlobalToast(
        error?.data?.message || "مشکلی در ثبت دیدگاه پیش آمد",
        "error",
      );
    }
  };

  const handleReplyClick = (comment) => {
    if (!token) {
      showGlobalToast("برای ثبت پاسخ ابتدا وارد شوید", "error");
      navigate("/auth");
      return;
    }
    setReplyTo(comment);
  };

  const handleCommentInputClick = () => {
    if (!token) {
      showGlobalToast("ابتدا وارد شوید", "error");
      navigate("/auth");
    }
  };

  // کامپوننت کارت نظر
  const CommentItem = ({ comment, isReply = false }) => (
    <Box
      sx={{
        position: "relative",
        pr: isReply ? { xs: 3, md: 6 } : 0,
        mt: isReply ? 2 : 0,
      }}
    >
      {isReply && (
        <Box
          sx={{
            position: "absolute",
            right: { xs: 12, md: 24 },
            top: -16,
            bottom: 24,
            width: 2,
            bgcolor: theme.palette.divider,
            borderRadius: 1,
            opacity: 0.7,
          }}
        />
      )}

      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Avatar
          src={getAvatarSrc(comment.userId?.image)}
          sx={{
            width: isReply ? 38 : 46,
            height: isReply ? 38 : 46,
            bgcolor: "primary.main",
            color: "primary.contrastText",
            boxShadow: `0 8px 16px ${theme.palette.primary.main}25`,
            border: `2px solid ${theme.palette.background.paper}`,
            fontWeight: "bold",
            zIndex: 1,
          }}
        >
          {comment.userId?.fullName?.charAt(0) || "U"}
        </Avatar>

        <Paper
          elevation={0}
          sx={{
            flex: 1,
            p: 2.5,
            borderRadius: 4,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: isReply
              ? "rgba(255, 255, 255, 0.015)"
              : "rgba(255, 255, 255, 0.04)",
            backdropFilter: "blur(12px)",
            transition: "all 0.25s ease-in-out",
            "&:hover": {
              borderColor: "primary.main",
              boxShadow: `0 8px 24px rgba(0, 0, 0, 0.15)`,
              bgcolor: isReply
                ? "rgba(255, 255, 255, 0.03)"
                : "rgba(255, 255, 255, 0.06)",
            },
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
            mb={1.5}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                color: theme.palette.text.primary,
                fontSize: "0.95rem",
              }}
            >
              {comment.userId?.fullName || "کاربر مهمان"}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                opacity: 0.8,
                whiteSpace: "nowrap",
                fontSize: "0.78rem",
              }}
            >
              {formatDateTime(comment.createdAt)}
            </Typography>
          </Stack>

          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              lineHeight: 1.9,
              whiteSpace: "pre-wrap",
              fontSize: "0.92rem",
            }}
          >
            {comment.text}
          </Typography>

          {!isReply && (
            <Stack direction="row" justifyContent="flex-end" mt={2}>
              <Button
                size="small"
                startIcon={
                  <Reply fontSize="small" sx={{ transform: "scaleX(-1)" }} />
                }
                onClick={() => handleReplyClick(comment)}
                sx={{
                  color: "text.secondary",
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  borderRadius: "8px",
                  px: 1.5,
                  py: 0.5,
                  transition: "all 0.2s",
                  "&:hover": {
                    bgcolor: "rgba(255, 255, 255, 0.08)",
                    color: "primary.main",
                  },
                }}
              >
                پاسخ به این نظر
              </Button>
            </Stack>
          )}
        </Paper>
      </Stack>
    </Box>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, md: 4 },
        borderRadius: 5,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: "background.paper",
        backgroundImage:
          "linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0) 100%)",
        boxShadow: "0 12px 40px rgba(0, 0, 0, 0.12)",
      }}
    >
      {/* هدر بخش نظرات */}
      <Stack direction="row" alignItems="center" spacing={2} mb={4}>
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: "16px",
            display: "grid",
            placeItems: "center",
            bgcolor: "primary.main",
            color: "primary.contrastText",
            boxShadow: `0 8px 20px ${theme.palette.primary.main}35`,
          }}
        >
          <Forum sx={{ fontSize: "1.4rem" }} />
        </Box>

        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 900, fontSize: "1.2rem", mb: 0.3 }}
          >
            نظرات کاربران
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ opacity: 0.8 }}
          >
            دیدگاه خود را با ما به اشتراک بگذارید
          </Typography>
        </Box>
      </Stack>

      {/* فرم ثبت دیدگاه */}
      <Box component="form" onSubmit={handleSubmit} sx={{ mb: 5 }}>
        {replyTo && (
          <Paper
            elevation={0}
            sx={{
              mb: 1.5,
              p: 1.5,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: "rgba(255,255,255,0.03)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "primary.main", fontWeight: 700 }}
            >
              در حال پاسخ به: {replyTo.userId?.fullName || "کاربر"}
            </Typography>

            <IconButton
              size="small"
              onClick={() => setReplyTo(null)}
              sx={{
                color: "text.secondary",
                "&:hover": { bgcolor: "rgba(255, 255, 255, 0.08)" },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Paper>
        )}

        <TextField
          fullWidth
          multiline
          minRows={4}
          placeholder={
            token
              ? replyTo
                ? "پاسخ خود را وارد کنید..."
                : "دیدگاه خود را بنویسید..."
              : "برای ثبت دیدگاه وارد حساب کاربری شوید"
          }
          value={text}
          onChange={(e) => setText(e.target.value)}
          onClick={handleCommentInputClick}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 4,
              bgcolor: "rgba(255,255,255,0.01)",
              transition: "all 0.25s ease",
              "& fieldset": {
                borderColor: theme.palette.divider,
              },
              "&:hover fieldset": {
                borderColor: "primary.main",
              },
              "&.Mui-focused fieldset": {
                borderWidth: "1.5px",
                borderColor: "primary.main",
              },
            },
            "& .MuiInputBase-input": {
              fontSize: "0.95rem",
              lineHeight: 2,
            },
          }}
        />

        <Stack direction="row" justifyContent="flex-end" mt={2.5}>
          <Button
            type="submit"
            variant="contained"
            disabled={!text.trim() || isSubmitting}
            endIcon={
              isSubmitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Send sx={{ transform: "rotate(180deg)" }} />
              )
            }
            sx={{
              px: 4,
              py: 1.2,
              borderRadius: 3,
              fontWeight: 800,
              boxShadow: `0 8px 24px ${theme.palette.primary.main}30`,
              minWidth: 160,
              textTransform: "none",
              transition: "all 0.2s",
              "&:hover": {
                bgcolor: "primary.dark",
                boxShadow: `0 12px 28px ${theme.palette.primary.main}45`,
              },
            }}
          >
            {replyTo ? "ثبت پاسخ" : "ثبت دیدگاه"}
          </Button>
        </Stack>
      </Box>

      {/* بخش نمایش کامنت‌ها */}
      {isLoading && page === 1 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress color="primary" size={32} />
        </Box>
      ) : isError ? (
        <Paper
          elevation={0}
          sx={{
            py: 5,
            px: 2,
            textAlign: "center",
            borderRadius: 4,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: "rgba(255,255,255,0.02)",
          }}
        >
          <Typography sx={{ opacity: 0.8, fontWeight: 700 }}>
            دریافت نظرات با مشکل مواجه شد. لطفاً دوباره تلاش کنید.
          </Typography>
        </Paper>
      ) : allComments.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            py: 6,
            px: 2,
            textAlign: "center",
            borderRadius: 4,
            border: `1px dashed ${theme.palette.divider}`,
            bgcolor: "rgba(255,255,255,0.015)",
          }}
        >
          <Forum
            sx={{ fontSize: 60, opacity: 0.3, mb: 1.5, color: "primary.main" }}
          />
          <Typography sx={{ opacity: 0.8, fontWeight: 700 }}>
            هنوز دیدگاهی ثبت نشده است. اولین نفر باشید!
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={3.5}>
          {allComments.map((comment) => (
            <Box key={comment._id}>
              <CommentItem comment={comment} />

              {(comment.replies || []).map((reply) => (
                <CommentItem key={reply._id} comment={reply} isReply />
              ))}
            </Box>
          ))}

          {/* المان راهنما (Sentinel) برای تشخیص انتهای لیست */}
          <div ref={lastCommentRef} style={{ height: 20 }} />

          {/* لودینگ صفحات بعدی در انتهای لیست */}
          {isFetching && page > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} color="primary" />
            </Box>
          )}
        </Stack>
      )}
    </Paper>
  );
}
